import React, { useState } from 'react';
import { MapPin, Mail, Phone, Send, Facebook, Twitter, Instagram, Loader2 } from 'lucide-react';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { contactData } from '../data/mock';
import axios from 'axios';
import { API_ENDPOINTS } from '../config/api';

const iconMap = { Facebook, Twitter, Instagram };

const Contact = () => {
  const [formData, setFormData] = useState({ name: '', email: '', subject: '', message: '' });
  const [sending, setSending] = useState(false);

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(API_ENDPOINTS.contact, formData);
      toast.success('Message envoye avec succes! Nous vous contacterons bientot.');
      setFormData({ name: '', email: '', subject: '', message: '' });
    } catch {
      toast.error("Erreur lors de l'envoi du message. Veuillez reessayer.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" className="py-24 bg-cream" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mb-12">
          <p className="uppercase text-xs md:text-sm tracking-[0.2em] font-bold text-forest mb-3 font-sans">
            Contact
          </p>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-serif font-bold text-navy tracking-tight mb-4">
            {contactData.title}
          </h2>
          <div className="w-16 h-1 bg-forest rounded-full mb-6" />
          <p className="text-base md:text-lg text-gray-600 font-sans">{contactData.subtitle}</p>
        </div>

        <div className="grid lg:grid-cols-5 gap-8">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="lg:col-span-2"
          >
            <div className="bg-navy rounded-3xl p-8 text-white h-full">
              <h3 className="text-2xl font-serif font-bold mb-8">Informations de Contact</h3>

              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-5 h-5" />
                  </div>
                  <div>
                    <h4 className="font-semibold mb-1 font-sans text-sm">Adresse</h4>
                    <p className="text-white/70 text-sm font-sans">{contactData.info.address}</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0">
                    <Mail className="w-5 h-5" />
                  </div>
                  <div>
                    <h4 className="font-semibold mb-1 font-sans text-sm">Email</h4>
                    <p className="text-white/70 text-sm font-sans">{contactData.info.email}</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0">
                    <Phone className="w-5 h-5" />
                  </div>
                  <div>
                    <h4 className="font-semibold mb-1 font-sans text-sm">Telephone</h4>
                    <p className="text-white/70 text-sm font-sans">{contactData.info.phone}</p>
                  </div>
                </div>
              </div>

              <div className="mt-10 pt-8 border-t border-white/10">
                <h4 className="font-semibold mb-4 font-sans text-sm">Suivez-nous</h4>
                <div className="flex space-x-3">
                  {contactData.socialMedia.map((social) => {
                    const Icon = iconMap[social.icon];
                    return (
                      <a
                        key={social.id}
                        href={social.url}
                        className="w-10 h-10 rounded-full bg-white/10 hover:bg-white hover:text-navy flex items-center justify-center transition-all duration-300"
                        aria-label={social.platform}
                      >
                        <Icon className="w-4 h-4" />
                      </a>
                    );
                  })}
                </div>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="lg:col-span-3"
          >
            <div className="bg-white rounded-3xl p-8 border border-black/5 shadow-sm">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid sm:grid-cols-2 gap-5">
                  <div className="space-y-2">
                    <Label htmlFor="name" className="font-sans text-sm">Nom complet *</Label>
                    <Input id="name" name="name" value={formData.name} onChange={handleChange} placeholder="Votre nom" required className="h-12 rounded-xl" data-testid="contact-name" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email" className="font-sans text-sm">Email *</Label>
                    <Input id="email" name="email" type="email" value={formData.email} onChange={handleChange} placeholder="votre@email.com" required className="h-12 rounded-xl" data-testid="contact-email" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="subject" className="font-sans text-sm">Sujet *</Label>
                  <Input id="subject" name="subject" value={formData.subject} onChange={handleChange} placeholder="Sujet de votre message" required className="h-12 rounded-xl" data-testid="contact-subject" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="message" className="font-sans text-sm">Message *</Label>
                  <Textarea id="message" name="message" value={formData.message} onChange={handleChange} placeholder="Ecrivez votre message ici..." required rows={5} className="resize-none rounded-xl" data-testid="contact-message" />
                </div>
                <button
                  type="submit"
                  disabled={sending}
                  data-testid="contact-submit-btn"
                  className="w-full inline-flex items-center justify-center px-6 py-4 rounded-full bg-navy text-white font-semibold text-base hover:bg-navy/90 disabled:opacity-50 transition-all duration-300 group font-sans"
                >
                  {sending ? (
                    <><Loader2 className="mr-2 w-5 h-5 animate-spin" />Envoi en cours...</>
                  ) : (
                    <>Envoyer le message<Send className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" /></>
                  )}
                </button>
              </form>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
